/** Mobile-optimized RN visit form for stages 5 (RN Visit) & 6 (RN Docs Completed).
 * Large-touch checklist, sticky CTA — designed for nurses in the field on a tablet. */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { CheckCircle2, Circle, ArrowRight, ChevronLeft, AlertTriangle, Stethoscope } from "lucide-react";
import AvidaMark from "@/components/AvidaMark";
import { stageColor } from "@/lib/palette";

export default function RnVisitPage() {
  const { cardId } = useParams();
  const nav = useNavigate();
  const [card, setCard] = useState(null);
  const [template, setTemplate] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/cards/${cardId}`).then(({ data }) => {
      setCard(data);
      setNote(data.note || "");
      return api.get("/templates").then(({ data: ts }) => {
        setTemplate(ts.find((t) => t.stage_number === data.current_stage));
      });
    });

  useEffect(() => { load(); }, [cardId]); // eslint-disable-line

  const toggle = async (idx) => {
    if (!card) return;
    const tasks = [...card.tasks_state];
    tasks[idx] = { ...tasks[idx], done: !tasks[idx].done };
    setCard({ ...card, tasks_state: tasks });
    try {
      await api.patch(`/cards/${card.id}/tasks/${idx}`, { done: tasks[idx].done });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const saveNote = async () => {
    try {
      await api.patch(`/cards/${card.id}`, { note });
      toast.success("Notes saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const advance = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cards/${card.id}/advance`);
      setCard(data);
      toast.success(`Advanced to Stage ${data.current_stage}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!card || !template) {
    return <div className="min-h-screen grid place-items-center text-sm text-[#15233B]/55">Loading…</div>;
  }

  const inScope = card.current_stage === 6 || card.current_stage === 7;
  const tasks = card.tasks_state || [];
  const done = tasks.filter((t) => t.done).length;
  const allDone = done === tasks.length && tasks.length > 0;
  const sc = stageColor(card.current_stage);

  return (
    <div className="min-h-screen flex flex-col bg-[#EEF2F8]" data-testid="rn-visit-page">
      <header className="sticky top-0 z-30 bg-[#15233B] text-white shadow-md relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_15%_40%,#FFCB05_0,transparent_50%)]"
        />
        <div className="px-4 py-3 flex items-center gap-3 relative">
          <button onClick={() => nav(-1)} data-testid="rn-back" className="p-2 -ml-2 rounded hover:bg-white/10">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <AvidaMark size="sm" withWordmark={false} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFCB05]">
              RN Field Visit
            </div>
            <div className="text-base font-extrabold truncate">{card.full_name}</div>
          </div>
          <span
            className="h-10 px-3 rounded-md grid place-items-center text-xs font-extrabold"
            style={{ backgroundColor: sc.primary, color: sc.text }}
          >
            {String(card.current_stage).padStart(2, "0")} · {template.name}
          </span>
        </div>
        <div className="h-[3px] bg-gradient-to-r from-transparent via-[#FFCB05] to-transparent" />
      </header>

      <main className="flex-1 px-4 py-5 max-w-2xl w-full mx-auto space-y-4">
        {!inScope && (
          <div className="bg-[#FFCB05]/10 border border-[#FFCB05]/40 rounded-lg p-3 text-sm text-[#15233B] flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[#FFCB05]" />
            This view is optimized for <b>Stage 5 (RN Visit)</b> and <b>Stage 6 (RN Docs Completed)</b>.
            This card is currently at Stage {card.current_stage}.
          </div>
        )}

        <section className="bg-white border border-[#15233B]/10 rounded-lg p-4">
          <div className="flex items-center gap-2 text-[#1E73C9]">
            <Stethoscope className="h-4 w-4" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold">Client</span>
          </div>
          <div className="mt-1 text-lg font-extrabold tracking-tight text-[#15233B]">{card.full_name}</div>
          <div className="text-xs text-[#15233B]/60 mt-0.5">
            <span className="font-mono">{card.av_id}</span> · {card.payer_mco}
            {card.county && <> · {card.county}</>}
          </div>
        </section>

        <section className="bg-white border border-[#15233B]/10 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#15233B]/60">
              Stage tasks
            </div>
            <div className="text-xs font-bold text-[#15233B]/55">{done}/{tasks.length}</div>
          </div>
          <ul className="space-y-1">
            {tasks.map((t, idx) => (
              <li key={idx}>
                <button
                  onClick={() => toggle(idx)}
                  data-testid={`rn-task-${idx}`}
                  className={`w-full flex items-start gap-3 p-3 rounded-md text-left transition-colors border ${
                    t.done ? "bg-[#E8F5E9] border-[#A5D6A7]" : "bg-[#F2F2F2] border-transparent hover:bg-[#FFCB05]/10"
                  }`}
                >
                  {t.done ? (
                    <CheckCircle2 className="h-6 w-6 text-[#2E7D32] shrink-0" />
                  ) : (
                    <Circle className="h-6 w-6 text-[#15233B]/35 shrink-0" />
                  )}
                  <span className={`text-base flex-1 ${t.done ? "line-through text-[#15233B]/55" : "text-[#15233B] font-semibold"}`}>
                    {t.title}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-white border border-[#15233B]/10 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#15233B]/60 mb-2">RN notes</div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={5}
            placeholder="Visit narrative, scoring observations, family/caregiver notes…"
            data-testid="rn-notes"
            className="w-full text-base border border-[#15233B]/15 rounded px-3 py-2 bg-white"
          />
          <button onClick={saveNote} data-testid="rn-save-notes" className="mt-2 text-sm font-bold text-[#1E73C9] hover:underline">
            Save notes
          </button>
        </section>
      </main>

      <div className="sticky bottom-0 bg-white border-t border-[#15233B]/10 px-4 py-3 shadow-[0_-4px_12px_rgba(21,35,59,0.05)]">
        <button
          onClick={advance}
          disabled={busy || !allDone}
          data-testid="rn-advance"
          className="w-full py-4 rounded-lg bg-[#FFCB05] hover:bg-[#FBC02D] text-[#15233B] text-base font-extrabold disabled:opacity-40 flex items-center justify-center gap-2"
        >
          {allDone ? (<>Advance to next stage <ArrowRight className="h-5 w-5" /></>) : (
            <>Finish all tasks to advance ({done}/{tasks.length})</>
          )}
        </button>
      </div>
    </div>
  );
}
